<script setup>
import { ref, computed, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { updateImportSession, getImportSessionRows } from '@/api/api.js'
import { useStore } from '@/stores/store'
import MdiChevronRight from '~icons/mdi/chevron-right'
import MdiInformationOutline from '~icons/mdi/information-outline'
import BaseSelect from '@/components/base/BaseSelect.vue'

const props = defineProps({
  session: { type: Object, required: true },
  columnMapping: { type: Object, required: true }
})
const emit = defineEmits(['advance'])

const { t } = useI18n()
const store = useStore()
const saving = ref(false)
const saveError = ref('')
const loadingRows = ref(true)
const loadError = ref('')

// All unique category names from the import file
const fileCategoryNames = ref([])

// hierarchy: { "LaptopsNew": null | "Electronics" }
// null means top-level
const hierarchy = ref({})

const existingCategories = computed(() => store.categories || [])

// Fast lookup: lowercase name → full category object
const existingByName = computed(() => {
  const map = {}
  existingCategories.value.forEach((c) => { map[c.name.toLowerCase()] = c })
  return map
})

// Fast lookup: id → category object (for resolving parent names)
const existingById = computed(() => {
  const map = {}
  existingCategories.value.forEach((c) => { map[c.id] = c })
  return map
})

function isExistingCat(name) {
  return name.toLowerCase() in existingByName.value
}

// Return the current parent name (string) for an existing DB category, or null
function currentParentName(name) {
  const cat = existingByName.value[name.toLowerCase()]
  if (!cat || !cat.parent) return null
  return existingById.value[cat.parent]?.name ?? null
}

// Parent options: top-level + all existing + other new categories in this file
const parentOptions = computed(() => {
  const options = [{ label: t('import.topLevel'), value: null }]
  existingCategories.value.forEach((c) => {
    options.push({ label: c.name, value: c.name })
  })
  fileCategoryNames.value.forEach((name) => {
    if (!isExistingCat(name)) {
      options.push({ label: t('import.catNewLabel', { name }), value: name })
    }
  })
  return options
})

onMounted(loadCategories)

async function loadCategories() {
  loadingRows.value = true
  loadError.value = ''
  try {
    // Always force-refresh categories so we have the latest DB state.
    await store.fetchCategories(true)

    // Load rows to extract category column values.
    const catCol = props.columnMapping?.category
    if (!catCol) return

    // Load all rows from the ORIGINAL parsed file (use_original=true) so we always
    // use CSV-header-keyed rows matching catCol, regardless of edited_rows state.
    let page = 1
    const names = new Set()
    while (true) {
      const res = await getImportSessionRows(props.session.id, page, 500, true)
      const data = res
      const rows = data.results || []
      rows.forEach((row) => {
        const val = row[catCol]
        if (val && typeof val === 'string' && val.trim()) {
          names.add(val.trim())
        }
      })
      if (page >= data.num_pages) break
      page++
    }
    fileCategoryNames.value = [...names].sort()

    // Init hierarchy for ALL file categories:
    // - If previously saved in session, use that value
    // - For new categories, default to top-level (null)
    // - For existing categories, default to their current parent name
    const saved = props.session.category_hierarchy || {}
    fileCategoryNames.value.forEach((name) => {
      if (name in saved) {
        hierarchy.value[name] = saved[name]
      } else if (isExistingCat(name)) {
        hierarchy.value[name] = currentParentName(name)
      } else {
        hierarchy.value[name] = null
      }
    })
  } catch {
    loadError.value = t('import.categoryLoadError')
  } finally {
    loadingRows.value = false
  }
}

async function advance() {
  if (loadError.value || loadingRows.value) return
  saving.value = true
  saveError.value = ''
  try {
    await updateImportSession(props.session.id, {
      category_hierarchy: hierarchy.value,
      status: 'reviewing'
    })
    emit('advance', { category_hierarchy: hierarchy.value })
  } catch {
    saveError.value = t('import.saveCategoryFailed')
  } finally {
    saving.value = false
  }
}
</script>

<template>
  <div class="flex flex-col gap-6">
    <p class="text-(--color-muted) text-sm">{{ $t('import.catIntro') }}</p>

    <div v-if="loadingRows" class="flex items-center gap-3 text-(--color-muted) text-sm">
      <span
        class="inline-block w-4 h-4 rounded-full border-2 border-(--color-accent) border-t-transparent animate-spin"
      />
      {{ $t('import.scanningCategories') }}
    </div>

    <div v-if="loadError" class="flex items-center gap-2 text-red-400 text-sm" role="alert">
      {{ loadError }}
      <button type="button" class="btn btn-ghost btn-small" @click="loadCategories">{{ $t('import.tryAgain') }}</button>
    </div>

    <template v-else-if="!loadError">
      <!-- No category column mapped -->
      <div v-if="!columnMapping?.category" class="state-empty">
        <MdiInformationOutline class="w-6 h-6" />
        <p>{{ $t('import.noCategoryColumn') }}</p>
      </div>

      <!-- No categories found in file -->
      <div
        v-else-if="fileCategoryNames.length === 0"
        class="rounded-xl border border-(--color-border) bg-(--color-surface) p-6 text-center text-(--color-muted) text-sm"
      >
        {{ $t('import.noCategoryValues') }}
      </div>

      <!-- All file categories -->
      <div v-else class="flex flex-col gap-3">
        <div v-for="name in fileCategoryNames" :key="name" class="data-card items-center">
          <div class="data-card__main">
            <p class="data-card__name">{{ name }}</p>
            <span v-if="!isExistingCat(name)" class="badge badge-accent text-xs">{{ $t('import.newBadge') }}</span>
            <span v-else class="badge badge-muted text-xs">{{ $t('import.existingBadge') }}</span>
          </div>
          <div class="data-card__actions flex items-center gap-2">
            <label class="text-(--color-muted) text-sm shrink-0">{{ $t('import.catParentLabel') }}</label>
            <BaseSelect
              :model-value="hierarchy[name] ?? ''"
              density="compact"
              :aria-label="$t('import.catParentLabel')"
              @update:model-value="hierarchy[name] = $event === '' ? null : $event"
            >
              <option v-for="opt in parentOptions" :key="String(opt.value)" :value="opt.value ?? ''">
                {{ opt.label }}
              </option>
            </BaseSelect>
          </div>
        </div>
      </div>
    </template>

    <p v-if="saveError" class="text-red-400 text-sm">{{ saveError }}</p>

    <div class="flex justify-end">
      <button
        class="btn btn-primary flex items-center gap-2"
        :disabled="saving || loadingRows || !!loadError"
        @click="advance"
      >
        {{ saving ? $t('common.saving') : $t('import.nextPreview') }}
        <MdiChevronRight class="w-4 h-4" />
      </button>
    </div>
  </div>
</template>
